// LiftVideo — a POINTER to a clip in the phone's library. Never a copy.
//
// THE CONSTRAINT THIS FILE EXISTS TO HOLD. An `attempt` stores `videoAssetId`
// and nothing else about the file: `ios:<PHAsset.localIdentifier>` or
// `android:<content URI>`. A lifter's history is hundreds of clips, and copying
// them into the app would double the storage of everything they have ever
// filmed — with the second copy being the one that goes stale, gets orphaned,
// and cannot be deleted from Photos.
//
// So nothing here ever holds video bytes. `resolve()` returns a small JPEG
// thumbnail and a duration; `play()` hands the clip to a player. That is all.
//
// WHY NOT THE `camera` ADDON. `addons/camera/` wraps `@capacitor/camera` and
// returns `{ dataUrl }` — a base64 copy of a still. The underlying plugin
// (8.2.4) does have `recordVideo`, `chooseFromGallery` and `playVideo`, but its
// result type is `MediaResult { uri, thumbnail, saved, webPath, metadata }`: a
// file URI into APP storage, with an `isPersistent` flag documented as "whether
// to store the video in persistent app storage or in temporary cache". No
// library identifier exists anywhere in that API, and there is no way to ask
// whether a clip is still in the library. It is the copy, not the pointer.
//
// THE WEB HAS NO ANSWER TO THIS. A browser cannot hold a durable reference to a
// file in the camera roll — the File System Access API is not on iOS Safari and
// does not reach the photo library anywhere. So every method rejects with
// `unsupported`, and the Film screen HIDES filming rather than offering a
// button that cannot work.

import { plugin, isNative } from '../core/native.js';

const PLUGIN = 'LiftVideo';

/** An error with a machine-readable `code`, so callers never match on prose. */
function fail(code, message) {
  return Object.assign(new Error(message || code), { code });
}

/**
 * The test hook, gated exactly like `window.__appfactoryQa` in main.js.
 *
 * The degradation states — a deleted clip, a clip outside limited access — are
 * required to be reachable in the UI, and there is no way to reach them from a
 * desktop browser: they are answers only a real photo library can give. This
 * lets QA supply those answers so the states can be driven and photographed.
 * It is inert in a real build, because nothing sets `__APPFACTORY_QA__` there.
 */
function qa() {
  if (typeof window === 'undefined' || !window.__APPFACTORY_QA__) return null;
  return window.__liftVideoQa ?? null;
}

/** The native implementation, or null when there is not one. */
function native() {
  if (!isNative) return null;
  return plugin(PLUGIN);
}

/**
 * Can this build film at all?
 *
 * A function rather than a constant because the answer is read at render time
 * and the QA hook is installed before boot. The Film screen calls it once.
 */
export function canFilm() {
  if (qa()) return true;
  return !!native();
}

/** Which platform wrote a stored pointer: 'ios' | 'android' | null. */
export function platformOf(assetId) {
  const s = String(assetId ?? '');
  if (s.startsWith('ios:')) return 'ios';
  if (s.startsWith('android:')) return 'android';
  return null;
}

/** Backing out of the camera is a normal outcome, not a failure. */
export function isCancelled(e) {
  return e?.code === 'cancelled' || /cancel/i.test(String(e?.message ?? ''));
}

/** True when this build simply cannot do video — the web. */
export function isUnsupported(e) {
  return e?.code === 'unsupported';
}

/**
 * Film a set. The clip is saved to the camera roll FIRST, and only its library
 * id comes back.
 * @returns {Promise<{ assetId: string }>}
 */
export async function record() {
  const hook = qa();
  if (hook?.record) return hook.record();

  const impl = native();
  if (!impl?.record) throw fail('unsupported', 'Filming needs the iOS or Android app.');

  const result = await impl.record();
  const assetId = result?.assetId;
  if (!assetId) throw fail('cancelled', 'Cancelled.');
  return { assetId };
}

/**
 * Choose a clip already in the library.
 * @returns {Promise<{ assetId: string } | null>} null when nobody picked one.
 */
export async function pick() {
  const hook = qa();
  if (hook?.pick) return hook.pick();

  const impl = native();
  if (!impl?.pick) throw fail('unsupported', 'Linking needs the iOS or Android app.');

  const result = await impl.pick();
  const assetId = result?.assetId;
  return assetId ? { assetId } : null;
}

/**
 * Is the clip still there, and what does it look like?
 *
 * NEVER REJECTS FOR A MISSING CLIP. A deleted clip and a clip outside a limited
 * selection are ordinary states of this screen, not errors — the screen has a
 * design for each. Only a build that cannot do video at all throws.
 *
 * @param {string} assetId
 * @returns {Promise<{ status: 'ok'|'missing'|'no-access', durationSeconds?: number, thumbnail?: string }>}
 */
export async function resolve(assetId) {
  const hook = qa();
  if (hook?.resolve) return hook.resolve(assetId);

  const impl = native();
  if (!impl?.resolve) throw fail('unsupported', 'Filming needs the iOS or Android app.');

  if (!assetId) return { status: 'missing' };

  const result = await impl.resolve({ assetId });
  const status = result?.status;
  return {
    status: status === 'ok' || status === 'no-access' ? status : 'missing',
    durationSeconds: Number.isFinite(result?.durationSeconds) ? result.durationSeconds : undefined,
    thumbnail: result?.thumbnail || undefined,
  };
}

/**
 * Watch it. In-app on iOS (Apple publishes no way to open one specific clip in
 * Photos), the gallery on Android.
 * @param {string} assetId
 */
export async function play(assetId) {
  const hook = qa();
  if (hook?.play) return hook.play(assetId);

  const impl = native();
  if (!impl?.play) throw fail('unsupported', 'Playback needs the iOS or Android app.');

  await impl.play({ assetId });
}

/**
 * Open this app's photo-access settings — the way out of a limited selection.
 *
 * Not part of the four-method contract, and required by it: "Photo access
 * doesn't include this clip" is only a fair thing to say if the screen also
 * offers the fix.
 */
export async function openPhotoSettings() {
  const hook = qa();
  if (hook?.openPhotoSettings) return hook.openPhotoSettings();

  const impl = native();
  if (!impl?.openPhotoSettings) throw fail('unsupported', 'No settings to open here.');

  await impl.openPhotoSettings();
}

export default { canFilm, record, pick, resolve, play, openPhotoSettings, platformOf, isCancelled, isUnsupported };
